"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScrollState = void 0;
const tslib_1 = require("tslib");
const sync = tslib_1.__importStar(require("thingies/lib/sync"));
class ScrollState {
    constructor(opts) {
        this.scrollTop$ = sync.val(0);
        this.scrollHeight$ = sync.val(0);
        this.clientHeight$ = sync.val(0);
        this.visible$ = sync.val(false);
        this.headerHeight$ = sync.val(0);
        this.footerHeight$ = sync.val(0);
        this.dragging$ = sync.val(false);
        this.railDragging$ = sync.val(false);
        this.viewportEl = null;
        this.railEl = null;
        this.thumbEl = null;
        this._hideTimer = 0;
        this._dragPointerOffset = 0;
        this._prevWebkitUserSelect = '';
        this._resizeObserver = null;
        this._rafId = 0;
        this.setViewport = (el) => {
            this.viewportEl = el;
        };
        this.setRail = (el) => {
            this.railEl = el;
        };
        this.setThumb = (el) => {
            this.thumbEl = el;
        };
        this._onScroll = () => {
            const el = this.viewportEl;
            if (!el)
                return;
            this.scrollTop$.next(el.scrollTop);
            this.showScrollbar();
        };
        this._onResize = () => {
            if (this._rafId)
                return;
            this._rafId = requestAnimationFrame(() => {
                this._rafId = 0;
                const el = this.viewportEl;
                if (!el)
                    return;
                const sh = el.scrollHeight;
                const ch = el.clientHeight;
                if (sh !== this.scrollHeight$.value)
                    this.scrollHeight$.next(sh);
                if (ch !== this.clientHeight$.value)
                    this.clientHeight$.next(ch);
            });
        };
        this.onThumbPointerDown = (e) => {
            if (e.button !== 0)
                return;
            e.currentTarget.setPointerCapture(e.pointerId);
            e.stopPropagation();
            const thumb = this.thumbEl;
            if (!thumb)
                return;
            this.dragging$.next(true);
            const rect = thumb.getBoundingClientRect();
            this._dragPointerOffset = e.clientY - rect.top;
            this._prevWebkitUserSelect = document.body.style.webkitUserSelect;
            document.body.style.webkitUserSelect = 'none';
            if (this.viewportEl)
                this.viewportEl.style.scrollBehavior = 'auto';
        };
        this.onThumbPointerMove = (e) => {
            if (!this.dragging$.value)
                return;
            const rail = this.railEl;
            if (!rail)
                return;
            const railRect = rail.getBoundingClientRect();
            const railHeight = railRect.height;
            const th = this.thumbHeight(railHeight);
            const pointerInRail = e.clientY - railRect.top;
            const thumbTop = pointerInRail - this._dragPointerOffset;
            const maxThumbTop = railHeight - th;
            const ratio = maxThumbTop > 0 ? thumbTop / maxThumbTop : 0;
            this.scrollToRatio(Math.max(0, Math.min(1, ratio)));
        };
        this.onThumbPointerUp = (e) => {
            const target = e.currentTarget;
            if (target.hasPointerCapture(e.pointerId))
                target.releasePointerCapture(e.pointerId);
            this.dragging$.next(false);
            this.railDragging$.next(false);
            document.body.style.webkitUserSelect = this._prevWebkitUserSelect;
            if (this.viewportEl)
                this.viewportEl.style.scrollBehavior = '';
            this._resetHideTimer();
        };
        this.onScrollbarPointerDown = (e) => {
            if (e.button !== 0)
                return;
            if (e.target !== e.currentTarget)
                return;
            e.currentTarget.setPointerCapture(e.pointerId);
            const rail = this.railEl;
            if (!rail)
                return;
            const railRect = rail.getBoundingClientRect();
            const ratio = (e.clientY - railRect.top) / railRect.height;
            this.scrollToRatio(Math.max(0, Math.min(1, ratio)));
            this.railDragging$.next(true);
            this._prevWebkitUserSelect = document.body.style.webkitUserSelect;
            document.body.style.webkitUserSelect = 'none';
            if (this.viewportEl)
                this.viewportEl.style.scrollBehavior = 'auto';
        };
        this.onScrollbarPointerMove = (e) => {
            if (!this.railDragging$.value)
                return;
            const rail = this.railEl;
            if (!rail)
                return;
            const railRect = rail.getBoundingClientRect();
            const ratio = (e.clientY - railRect.top) / railRect.height;
            this.scrollToRatio(Math.max(0, Math.min(1, ratio)));
        };
        this.onScrollbarPointerUp = (e) => {
            const target = e.currentTarget;
            if (target.hasPointerCapture(e.pointerId))
                target.releasePointerCapture(e.pointerId);
            if (!this.railDragging$.value)
                return;
            this.railDragging$.next(false);
            this.dragging$.next(false);
            document.body.style.webkitUserSelect = this._prevWebkitUserSelect;
            if (this.viewportEl)
                this.viewportEl.style.scrollBehavior = '';
            this._resetHideTimer();
        };
        this.onScrollbarWheel = (e) => {
            this.scrollBy(e.deltaY);
            e.preventDefault();
        };
        this.onScrollbarPointerEnter = () => {
            this.showScrollbar();
        };
        this.railWidth$ = sync.val(opts?.railWidth ?? 8 /* SCROLL_AREA.WIDTH */);
        this.minThumbSize = opts?.minThumbSize ?? 24 /* SCROLL_AREA.MIN_THUMB_SIZE */;
        this.hideDelay = opts?.hideDelay ?? 2000 /* SCROLL_AREA.HIDE_DELAY */;
        this.alwaysVisible$ = sync.val(opts?.alwaysVisible ?? false);
        const { scrollTop$, scrollHeight$, clientHeight$ } = this;
        this.maxScrollTop$ = sync.comp([scrollHeight$, clientHeight$], ([scrollHeight, clientHeight]) => Math.max(0, scrollHeight - clientHeight));
        this.scrollRatio$ = sync.comp([scrollTop$, this.maxScrollTop$], ([scrollTop, maxScrollTop]) => maxScrollTop > 0 ? scrollTop / maxScrollTop : 0);
        this.thumbRatio$ = sync.comp([clientHeight$, scrollHeight$], ([clientHeight, scrollHeight]) => scrollHeight > 0 ? clientHeight / scrollHeight : 1);
        this.canScroll$ = sync.comp([scrollHeight$, clientHeight$], ([scrollHeight, clientHeight]) => scrollHeight > clientHeight);
    }
    scrollTo(top) {
        const el = this.viewportEl;
        if (!el)
            return;
        el.scrollTop = Math.max(0, Math.min(top, this.maxScrollTop$.value));
    }
    scrollToRatio(ratio) {
        this.scrollTo(Math.max(0, Math.min(1, ratio)) * this.maxScrollTop$.value);
    }
    scrollBy(delta) {
        const el = this.viewportEl;
        if (!el)
            return;
        el.scrollTop += delta;
    }
    showScrollbar() {
        if (!this.visible$.value)
            this.visible$.next(true);
        this._resetHideTimer();
    }
    hideScrollbar() {
        if (this.visible$.value)
            this.visible$.next(false);
        window.clearTimeout(this._hideTimer);
    }
    _resetHideTimer() {
        window.clearTimeout(this._hideTimer);
        if (this.alwaysVisible$.value)
            return;
        this._hideTimer = window.setTimeout(() => {
            if (!this.dragging$.value)
                this.visible$.next(false);
        }, this.hideDelay);
    }
    thumbHeight(railHeight) {
        return Math.max(this.minThumbSize, this.thumbRatio$.value * railHeight);
    }
    thumbTop(railHeight) {
        const th = this.thumbHeight(railHeight);
        return this.scrollRatio$.value * (railHeight - th);
    }
    start() {
        const el = this.viewportEl;
        if (el) {
            el.addEventListener('scroll', this._onScroll, { passive: true });
            this.scrollHeight$.next(el.scrollHeight);
            this.clientHeight$.next(el.clientHeight);
            this.scrollTop$.next(el.scrollTop);
        }
        this._resizeObserver = new ResizeObserver(this._onResize);
        if (el) {
            this._resizeObserver.observe(el);
            const content = el.firstElementChild;
            if (content)
                this._resizeObserver.observe(content);
        }
        if (this.alwaysVisible$.value) {
            this.visible$.next(true);
        }
        const onSelectionChange = () => {
            this._resetHideTimer();
        };
        document.addEventListener('selectionchange', onSelectionChange);
        return () => {
            if (el)
                el.removeEventListener('scroll', this._onScroll);
            this._resizeObserver?.disconnect();
            this._resizeObserver = null;
            window.clearTimeout(this._hideTimer);
            if (this._rafId) {
                cancelAnimationFrame(this._rafId);
                this._rafId = 0;
            }
            document.removeEventListener('selectionchange', onSelectionChange);
        };
    }
}
exports.ScrollState = ScrollState;
